import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Label,
  Input,
  Spinner,
  CardHeader,
  Button,
  FormGroup,
} from "reactstrap";
import { toast } from "react-toastify";
import axiosInstance from "../../helpers/axiosInstance";

const Breadcrumbs = ({ title, breadcrumbItems }) => (
  <div>
    <h3>{title}</h3>
    <ul>
      {breadcrumbItems.map((item) => (
        <li key={item.title}> {item.title} </li>
      ))}
    </ul>
  </div>
);

const normalizeProductVisibility = (productVisibility = {}) => {
  const category = productVisibility.category;

  return {
    mode: productVisibility.mode || "all",
    category: typeof category === "object" && category !== null ? category._id : category || "",
  };
};

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [codSaving, setCodSaving] = useState(false);
  const [visibilitySaving, setVisibilitySaving] = useState(false);
  const [codEnabled, setCodEnabled] = useState(false);
  const [categories, setCategories] = useState([]);
  const [productVisibility, setProductVisibility] = useState({
    mode: "all",
    category: "",
  });

  const breadcrumbItems = [
    { title: "Toy Hub Admin", link: "/" },
    { title: "Admin Settings", link: "#" },
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const [settingsResponse, categoriesResponse] = await Promise.all([
        axiosInstance.get("/api/v1/admin/settings"),
        axiosInstance.get("api/v1/categories"),
      ]);

      const settings = settingsResponse.data.settings || {};
      setCodEnabled(Boolean(settings.cod));
      setProductVisibility(normalizeProductVisibility(settings.productVisibility));
      setCategories(categoriesResponse.data.categories || []);
    } catch (error) {
      console.error("Error fetching admin settings:", error);
      toast.error("Failed to fetch admin settings");
    } finally {
      setLoading(false);
    }
  };

  const updateCODSettings = async (nextCodEnabled) => {
    try {
      setCodSaving(true);
      const response = await axiosInstance.put("/api/v1/admin/settings/cod", {
        cod: nextCodEnabled,
      });
      const settings = response.data.settings || {};

      setCodEnabled(Boolean(settings.cod));
      toast.success("COD settings updated successfully!");
    } catch (error) {
      console.error("Error updating COD settings:", error);
      toast.error("Failed to update COD settings");
    } finally {
      setCodSaving(false);
    }
  };

  const handleVisibilityModeChange = (event) => {
    const mode = event.target.value;

    setProductVisibility((previous) => ({
      mode,
      category: mode === "all" ? "" : previous.category,
    }));
  };

  const handleVisibilityCategoryChange = (event) => {
    setProductVisibility((previous) => ({
      ...previous,
      category: event.target.value,
    }));
  };

  const updateProductVisibilitySettings = async () => {
    if (productVisibility.mode === "category" && !productVisibility.category) {
      toast.error("Please select a category");
      return;
    }

    try {
      setVisibilitySaving(true);
      const payload = {
        productVisibility: {
          mode: productVisibility.mode,
          category: productVisibility.mode === "category" ? productVisibility.category : null,
        },
      };
      const response = await axiosInstance.put(
        "/api/v1/admin/settings/product-visibility",
        payload
      );

      setProductVisibility(
        normalizeProductVisibility(response.data.productVisibility)
      );
      toast.success("Product visibility settings updated successfully!");
    } catch (error) {
      console.error("Error updating product visibility settings:", error);
      toast.error("Failed to update product visibility settings");
    } finally {
      setVisibilitySaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Admin Settings" breadcrumbItems={breadcrumbItems} />

          <Row className="mb-4">
            <Col lg={12}>
              <Card>
                <CardHeader className="bg-soft-primary">
                  <h5 className="card-title mb-0">Payment Settings</h5>
                </CardHeader>
                <CardBody>
                  <Row className="align-items-center">
                    <Col md={6}>
                      <div>
                        <h5 className="font-size-14 mb-0">Cash On Delivery (COD)</h5>
                        <p className="text-muted mb-0">Enable or disable COD payment option for customers</p>
                      </div>
                    </Col>
                    <Col md={6} className="d-flex justify-content-end">
                      <div className="form-check form-switch form-switch-lg">
                        <Input
                          type="switch"
                          id="codToggle"
                          name="codToggle"
                          className="form-check-input"
                          checked={codEnabled}
                          disabled={codSaving}
                          onChange={() => updateCODSettings(!codEnabled)}
                        />
                        <Label className="form-check-label ms-2" for="codToggle">
                          {codSaving ? "Saving..." : codEnabled ? "Enabled" : "Disabled"}
                        </Label>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col lg={12}>
              <Card>
                <CardHeader className="bg-soft-primary">
                  <h5 className="card-title mb-0">Product Visibility Settings</h5>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="productVisibilityMode">Visibility Mode</Label>
                        <Input
                          type="select"
                          id="productVisibilityMode"
                          value={productVisibility.mode}
                          onChange={handleVisibilityModeChange}
                          disabled={visibilitySaving}
                        >
                          <option value="all">Show all products</option>
                          <option value="category">Show products from one category</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="productVisibilityCategory">Visible Category</Label>
                        <Input
                          type="select"
                          id="productVisibilityCategory"
                          value={productVisibility.category}
                          onChange={handleVisibilityCategoryChange}
                          disabled={productVisibility.mode !== "category" || visibilitySaving}
                        >
                          <option value="">Select category</option>
                          {categories.map((category) => (
                            <option key={category._id} value={category._id}>
                              {category.name}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>
                  <div className="d-flex justify-content-end">
                    <Button
                      color="primary"
                      onClick={updateProductVisibilitySettings}
                      disabled={
                        visibilitySaving ||
                        (productVisibility.mode === "category" && !productVisibility.category)
                      }
                    >
                      {visibilitySaving ? <Spinner size="sm" /> : "Save Product Visibility"}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AdminSettings;
